import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import styled from 'styled-components';
import { PrimaryColor, PastelPrimaryColor } from '../../styles/global/css/Utils';
import { Label } from '../dropdown/style/Style';
import { InputProps } from './model/Model';
import { Asterisk, Container, InputBlock, InputContainer, InputLogo, LabelContainer, TextArea } from './style/Style';
import { INPUT_HEIGHT } from '../../styles/global/css/GlobalInput';

export const SHOW_PASSWORD_SIZE = 35;

export const HeaderTitleInput = styled.input`
	font-weight: 700;
	font-size: 15px;
	max-width: 100%;
	text-overflow: ellipsis;
	border: none;
	&:hover {
		border-bottom: 1px solid ${PrimaryColor};
    	margin-bottom: -1px;
	}
	&:focus {
    	background-color: ${PastelPrimaryColor};
  	}
`;

export function blurOnEnterPressed(e: React.KeyboardEvent<HTMLInputElement>) {
	if (e.key === 'Enter') {
		(document.activeElement as HTMLElement).blur();
	}
}

function ValueChange(prevValue: string | number, currentValue: string | number, props: InputProps, setValue, canUpdate: boolean): void {
	if (props.normalize != null) {
		props.normalize.forEach(f => {
			currentValue = f(currentValue);
		});
	}

	let valid = true;

	if (props.validate != null) {
		props.validate.forEach(f => {
			if (valid) {
				valid = f(currentValue);
			}
		});
	}

	if (valid) {
		setValue(currentValue);
		if ((props.onChange != null) && canUpdate) {
			props.onChange(currentValue);
		}
	} else {
		setValue(prevValue);
		if ((props.onChange != null) && canUpdate) {
			props.onChange(prevValue);
		}
	}
}

function Input<T = string>(props: InputProps<T>): JSX.Element {
	const [value, setValue] = React.useState<string | number>(props.value ?? '');
	const [canUpdate, setCanUpdate] = React.useState<boolean>(!props.delay);
	const [timeoutId, setTimeoutId] = React.useState<number | undefined>(undefined);
	const width = props.inputStyle?.width ? props.inputStyle?.width : '300px';
	const textAlign = props.inputStyle?.textAlign ? 'center' : 'left';
	const [showPassword, setShowPassword] = React.useState<boolean>(false);

	React.useEffect(() => {
		setValue(props.value ?? '');
	}, [props.value]);

	function update(value: any): void {
		clearTimeout(timeoutId);
		setCanUpdate(false);

		const id = setTimeout(() => {
			setTimeoutId(undefined);
			setCanUpdate(true);
			props.onChange?.(value);
		}, props.delay, canUpdate, timeoutId, value);

		setTimeoutId(id);
	}

	const onValueChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		if (!props.disableChange) {
			if (props.delay) update(e.target.value);
			setValue(e.target.value);
			ValueChange(value, e.target.value, props, setValue, canUpdate);
		}
	};

	return (
		<Container flexDirection={props.inputStyle?.labelUp ? 'column' : 'row'} height={props.inputStyle?.containerHeight ?? props.inputStyle?.height} marginLeft={props.inputStyle?.marginLeft} marginRight={props.inputStyle?.marginRight} width={props.inputStyle?.containerWidth} padding={props.paddingLabel ? '21px 0 0 0' : undefined} margin={props.inputStyle?.margin} flexShrink={props.inputStyle?.flexShrink} alignSelf={props.inputStyle?.alignSelf}>
			{
				props.label &&
				<LabelContainer>
					{
						props.required &&
						<Asterisk />
					}
					<Label {...props.inputStyle?.labelStyle}>{props.label}</Label>
				</LabelContainer>
			}
			<InputContainer cursor={props.inputStyle?.cursor} hasBorder={props.inputStyle?.hasInputBorder} borderRadius={props.inputStyle?.borderRadius} backgroundColor={props.inputStyle?.backgroundColor} height={props.inputStyle?.height}>
				{
					(props.JSXContent != null) &&
					<InputLogo maxHeight={props.inputStyle?.height}>
						{props.JSXContent}
					</InputLogo>
				}
				{props.textArea
					? <TextArea padding={props.inputStyle?.padding}
						placeholderColor={props.inputStyle?.placeholderColor}
						border={props.inputStyle?.border}
						borderRadius={props.inputStyle?.borderRadius}
						style={{ width, textAlign, height: props.inputStyle?.height }}
						id={props.id}
						name={props.name}
						type={props.type}
						onChange={onValueChange}
						value={value}
						required={props.required}
						disabled={props.disabled}
						placeholder={props.placeholder}
						maxLength={props.maxLength}
						onClick={props.onClick}
						spellCheck={props.spellcheck}
						onBlur={e => props.onBlur?.(e)}
						{...props.extraInputAttribute}
					/>
					: <><InputBlock padding={props.inputStyle?.padding}
						backgroundColor={props.inputStyle?.backgroundColor}
						isPassword={props.type === 'password'}
						removePlaceHolderOnFocus={props.inputStyle?.removePlaceHolderOnFocus}
						placeholderColor={props.inputStyle?.placeholderColor}
						borderRadius={props.inputStyle?.borderRadius}
						style={{ width, textAlign, height: props.inputStyle?.height, cursor: props.inputStyle?.cursor, textOverflow: props.inputStyle?.textOverflow }}
						id={props.id}
						name={props.name}
						type={showPassword ? 'text' : props.type}
						border={props.inputStyle?.border}
						fontSize={props.inputStyle?.fontSize}
						onChange={onValueChange}
						value={value}
						step={props.step}
						required={props.required}
						disabled={props.disabled}
						placeholder={props.placeholder}
						maxLength={props.maxLength}
						onClick={props.onClick}
						fontWeight={props.inputStyle?.fontWeight}
						color={props.inputStyle?.color}
						{...props.extraInputAttribute}
						onKeyDownCapture={blurOnEnterPressed}
						onBlur={e => props.onBlur?.(e)}
						mode={props.mode}
						min={props.min}
						max={props.max}
						pattern={props.pattern}
						autoFocus={props.autoFocus}
					/>
					{props.type === 'password' ? <PasswordBlock setShowPassword={setShowPassword} showPassword={showPassword} height={props.inputStyle?.height} /> : null}
					</>
				}

			</InputContainer>
		</Container>
	);
}

function PasswordBlock(props: { setShowPassword: React.Dispatch<React.SetStateAction<boolean>>, showPassword: boolean, height?: string }) {
	return <PasswordBlockContainer onClick={() => props.setShowPassword(!props.showPassword)} height={props.height}>
		{props.showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
	</PasswordBlockContainer>;
}

const PasswordBlockContainer = styled.div<{ height?: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${SHOW_PASSWORD_SIZE}px;
	height: ${p => p.height ?? `${INPUT_HEIGHT}px`};
	margin-left: -${SHOW_PASSWORD_SIZE}px;
	border-radius: 0 5px 5px 0;
	cursor: pointer;
`;

export default Input;
